<script lang="ts">
  /**
   * Warnung, Feststellung, Lücke, Offline-Stand, Erklärung.
   *
   * ## Der einzige Hinweis
   *
   * Bis zur Umstellung der Flächen stand daneben ein zweiter unter
   * `gestaltung/Hinweis.svelte` — die schmalere Fassung, die den Panels
   * gehörte, ohne die Stufe `leise` und ohne `ausweg`. Zwei Dateien mit
   * demselben Namen in zwei Ordnern waren der Preis dafür, die Bauteile zu
   * bauen, ohne im selben Zug elf Flächen umzubauen. Seit alle Flächen hier
   * hereinlaufen, ist sie gelöscht — wie es an dieser Stelle angekündigt war.
   *
   * ## Die drei Stufen für Fehlendes
   *
   * Sechs API-Aufrufe liefen monatelang unbemerkt in einen 404. Möglich war das
   * durch `try?` — ein Aufruf, dessen Fehler weggeworfen wird. Die Oberfläche
   * sah dabei nie kaputt aus; sie zeigte einfach nichts, und „nichts" ist von
   * „nichts vorhanden" nicht zu unterscheiden.
   *
   * Nicht jedes verschluckte Beiwerk ist falsch — „das Wetter ist Beiwerk,
   * dafür soll die Ansicht nicht scheitern" ist eine richtige Entscheidung.
   * Aber die Gestaltung muss den Unterschied tragen können. Deshalb drei
   * Stufen:
   *
   * | Stufe | Wann | Wie |
   * |---|---|---|
   * | **still** | Beiwerk fehlt, die Fläche bleibt vollständig | **gar kein Hinweis** — aber die Fläche darf nicht so tun, als sei die Angabe „0" |
   * | `leise` | ein Teil fehlt, der Rest stimmt | eine ruhige Zeile **am Ort der Lücke**: was fehlt und warum |
   * | `ernst` | die Fläche kann ihre Aufgabe nicht erfüllen | Grund **und Ausweg** |
   *
   * Die stille Stufe ist bewusst kein Wert: Sie besteht darin, nichts zu
   * rendern. Wer dafür eine Komponente sucht, hat die Stufe verwechselt.
   *
   * Das Tagebuch-Panel macht die mittlere Stufe schon vor: „Ergänzt: Ort,
   * Temperatur" bzw. „Ohne Ladestand: Das Auto war nicht erreichbar." Genau so —
   * die Lücke wird benannt, damit sie nicht wie ein Fehler aussieht.
   *
   * ## Die übrigen Arten
   *
   * - **merk** — eine Feststellung des Planers, keine Warnung („Übernachtung
   *   nötig"). Bernstein, nicht Rot.
   * - **ruhig** — der Offline-Fall. „Kein Netz" ist unterwegs der Normalzustand,
   *   kein Fehler; ein rotes Fehlerbanner wäre hier schlicht gelogen. Offline
   *   ist **nie** `ernst`.
   * - **erklaerung** — der begründende Fußnotentext, der überall im Produkt
   *   steht: kein Kasten, keine Farbe, nur kleiner und ruhiger.
   */
  import type { Snippet } from 'svelte';
  import Zeichen from '../Zeichen.svelte';
  import { hinweisZeichen, type Hinweisart } from './muster';

  interface Props {
    art?: Hinweisart;
    titel?: string | null;
    /** Der Knopf oder Link, der aus der Lage herausführt. Ab `leise` Pflicht. */
    ausweg?: Snippet;
    children: Snippet;
  }
  let { art = 'erklaerung', titel = null, ausweg, children }: Props = $props();

  const symbol = $derived(hinweisZeichen(art));
</script>

{#if art === 'erklaerung'}
  <p class="erklaerung">{@render children()}</p>
{:else}
  <div class="kasten {art}" role={art === 'ernst' ? 'alert' : 'note'}>
    {#if symbol}
      <span class="zeichen"><Zeichen name={symbol} groesse={15} /></span>
    {/if}
    <div class="inhalt">
      {#if titel}<strong>{titel}</strong>{/if}
      <div>{@render children()}</div>
      <!-- Ab „leise" gehört ein Ausweg dazu. Ein Hinweis ohne ihn ist eine
           Entschuldigung: Er sagt, dass etwas nicht ging, und lässt den Nutzer
           damit stehen. -->
      {#if ausweg}<div class="ausweg">{@render ausweg()}</div>{/if}
    </div>
  </div>
{/if}

<style>
  .erklaerung {
    margin: 0;
    padding-top: var(--raum-4);
    font-size: var(--fs-fein);
    color: var(--text-dim);
    line-height: var(--zh-normal);
  }

  .kasten {
    display: flex;
    gap: var(--raum-4);
    align-items: flex-start;
    padding: var(--raum-4) var(--raum-5);
    border-radius: var(--radius-klein);
    border-left: 3px solid;
    color: var(--text);
    font-size: var(--fs-zeile);
    line-height: var(--zh-normal);
  }
  .zeichen {
    flex: none;
    padding-top: 1px;
  }
  .inhalt {
    display: grid;
    gap: var(--raum-3);
    min-width: 0;
  }
  .inhalt strong {
    font-weight: var(--gw-halb);
  }

  .ernst {
    background: var(--zustand-meiden-flaeche);
    border-left-color: var(--zustand-meiden);
  }
  .ernst .zeichen,
  .ernst strong {
    color: var(--zustand-meiden);
  }

  .merk {
    background: var(--merk-flaeche);
    border-left-color: var(--merk-linie);
  }
  .merk strong {
    color: var(--merk);
  }

  .ruhig {
    background: var(--surface-2);
    border-left-color: var(--border);
  }
  .ruhig .zeichen,
  .ruhig strong {
    color: var(--text-dim);
  }

  /* `leise` bekommt keine Fläche und keine Farbe: Sie benennt eine Lücke am
     Ort der Lücke und darf dort nicht lauter sein als die Zeile daneben. Nur
     ein Strich links, damit erkennbar bleibt, dass hier etwas gesagt wird. */
  .leise {
    background: transparent;
    border-left-color: var(--border-stark);
    padding: 3px 0 3px 9px;
    color: var(--text-dim);
  }
  .leise strong {
    color: var(--text-dim);
  }
</style>
